import React, { Dispatch, SetStateAction } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { List, ListItem, Typography } from '@material-ui/core';
import BaseButton from 'features/app/components/BaseButton';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import DoneIcon from '@material-ui/icons/Done';
import { PlansData } from 'features/subscription/ts/SubscriptionPlans';
import { redirectCheckoutSession } from 'features/subscription/api';
import { useTranslation } from 'hooks/useTranslation';
import { getCardAvailableActions } from 'features/subscription/utils';
import { addErrorMessage } from 'features/message/redux/action';
import { useDispatch } from 'react-redux';

const useStyles = makeStyles(() => ({
  pricingBox: {
    borderRadius: 5,
    backgroundColor: '#fff',
    boxShadow: '0px 10px 35px #0000002F',
    maxWidth: 475,
    width: '100%',
    overflow: 'hidden',
  },
  popularBox: {
    '& $price': {
      '& span': {
        color: '#37b37e',
      },
    },
  },
  popularTag: {
    backgroundColor: '#38b2ae',
    color: '#fff',
    display: 'block',
    textAlign: 'center',
    padding: '25px',
    fontWeight: 500,
    fontSize: 22,
  },
  priceBoxWrap: {
    padding: '20px 60px 60px 60px',
  },
  price: {
    marginBottom: 20,
    color: '#5A7184',
    '& span': {
      fontSize: 40,
    },
  },
  priceBoxHead: {
    paddingBottom: 30,
    borderBottom: '1px solid #E5EAF4',
    '& h4': {
      fontSize: 24,
      marginBottom: 20,
      fontWeight: 600,
      color: '#183B56',
    },
    '& p': {
      fontSize: 14,
      color: '#5A7184',
    },
  },
  priceBtn: {
    padding: '20px',
    justifyContent: 'flex-start',
    '& span': {
      width: '100%',
      textAlign: 'left',
    },
    '& svg': {
      verticalAlign: 'middle',
      float: 'right',
    },
  },
  priceList: {
    margin: '30px 0',
    '& .MuiListItem-root': {
      paddingLeft: 40,
      color: '#183B56',
      fontSize: 18,
      marginBottom: 20,
      '& svg': {
        position: 'absolute',
        left: 0,
        top: 8,
        borderRadius: '100%',
        backgroundColor: 'rgba(54, 179, 126, 0.15)',
        padding: 5,
        color: '#36B37E',
        strokeWidth: 1,
        stroke: '#36B37E',
        height: 27,
        width: 27,
      },
    },
  },
}));

type Props = {
  value?: PlansData;
  defaultSelected?: string;
  currency: string;
  subscriptionButtonDisabled: boolean;
  setSubscriptionButtonDisabled: Dispatch<SetStateAction<boolean>>;
};

const CustomCard: React.FC<Props> = ({
  defaultSelected,
  value,
  currency,
  subscriptionButtonDisabled,
  setSubscriptionButtonDisabled,
}) => {
  const classes = useStyles();
  const { t } = useTranslation('subscription');
  const selectedPlan = value?.prices.filter(
    (data: { recurring_interval: string; currency: string }) =>
      data?.recurring_interval === defaultSelected && data.currency === currency
  )?.[0];
  const dispatch = useDispatch();
  const isPremiumPlan = value?.name.toLowerCase() == 'premium';

  const handleSelectPlan = () => {
    const priceId = selectedPlan?.id;
    if (priceId) {
      setSubscriptionButtonDisabled(true);
      redirectCheckoutSession(priceId)
        .then(res => {
          window.location.assign(res.data.url);
        })
        .catch(error => {
          setSubscriptionButtonDisabled(false);
          const errorResponse = error?.response;
          const errorMsg = errorResponse?.data?.[0];
          if (errorResponse?.status === 400 && errorMsg) {
            dispatch(addErrorMessage(errorMsg));
          }
        });
    }
  };

  return (
    <div className={`${classes.pricingBox} ${classes.popularBox}`}>
      {value?.name === 'Premium' ? (
        <Typography className={classes.popularTag} component="span" variant="body1">
          {t('cards.mostPopular')}
        </Typography>
      ) : null}
      <div className={classes.priceBoxWrap}>
        <div className={classes.price}>
          <span>
            {(selectedPlan?.unit_amount || 0) / 100} {selectedPlan?.currency?.toUpperCase()}
          </span>{' '}
          /{defaultSelected === 'month' ? t('month') : t('year')}
        </div>
        <div className={classes.priceBoxHead}>
          <Typography component="h4" variant="h4">
            {isPremiumPlan ? t('plans.premium') : t('plans.basic')}
          </Typography>
          <Typography component="p" variant="body2">
            {isPremiumPlan ? t('cards.premiumDescription') : t('cards.basicDescription')}
          </Typography>
        </div>
        <List className={classes.priceList}>
          {Object.values(value?.metadata || {}).map((meta, i) => (
            <ListItem disableGutters key={i}>
              <DoneIcon />
              {`${t('upto')} ${meta} ${t('users')}`}
            </ListItem>
          ))}
          {getCardAvailableActions(t).map((data, i) => {
            return (
              <ListItem disableGutters key={i}>
                <DoneIcon />
                {data.title}
              </ListItem>
            );
          })}
        </List>
        <BaseButton className={classes.priceBtn} onClick={handleSelectPlan} disabled={subscriptionButtonDisabled}>
          {t('cards.getStarted')} <ArrowForwardIcon />
        </BaseButton>
      </div>
    </div>
  );
};

export default CustomCard;
